<template>
  <auth-layout :page="'signup'">
    <h1 class="text-xl mb-2 text-bold mt-4">Create your agency workspace</h1>

    <p v-show="e1 != 3" class="text-xs mb-4">
      Create your account, add your brand, and set up your first business. You can save your progress and return anytime.
    </p>

    <details class="mb-5 text-sm"><summary style="cursor:pointer">What will I need for setup?</summary><agency-setup-guide /></details>

    <div>
      <div
        v-if="success"
        class="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4"
        role="alert"
      >
        {{ success }}
      </div>

      <div
        v-if="error"
        class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
        role="alert"
      >
        <div>{{ error }}</div>
      </div>

      <v-stepper v-model="e1">
        <v-stepper-header style="display: none;">
          <v-stepper-step :complete="e1 > 1" step="1">
            Account Setup
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="e1 > 2" step="2">
            Company Information
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <!-- Products -->
            <div class="mb-6">
              <p class="mb-2 text-xs font-bold">Product Selected</p>

              <div class="border rounded p-3 pb-1 mb-3">
                <label class="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="white_label_signup_enabled"
                    class="mt-1 mr-3"
                    readonly
                  />
                  <div>
                    <div class="font-bold text-sm">LSA Command — White Label</div>
                    <div class="text-xs text-gray-600 mt-1">
                      Add your LSA CID, connect and manage accounts under your own brand, pull LSA stats and leads, create reports, and submit LSA surveys—all from a customizable white-label platform.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">First Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-first-name"
                    type="text"
                    ref="first_name"
                    placeholder="e.g. John"
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Last Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-last-name"
                    type="text"
                    ref="last_name"
                    placeholder="e.g. Doe"
                  />
                </div>
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Agency Name</p>
                  <input
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    id="grid-city"
                    type="text"
                    ref="company_name"
                    v-model="company_name"
                    placeholder="e.g. My Company Inc."
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Phone Number</p>
                  <input
                    type="text"
                    ref="phone"
                    v-model="phone"
                    @keyup="phone_valid = false"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    placeholder="e.g. +18123124214"
                  />
                </div>
              </div>
            </div>

            <div class="-mx-3">
              <div class="flex flex-wrap mb-2">
                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Email</p>
                  <input
                    type="email"
                    ref="email"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    placeholder="e.g. johndoe@example.com"
                  />
                </div>

                <div class="w-full md:w-1/2 px-3 pt-2">
                  <p class="mb-1 text-xs mb-2">Password</p>
                  <input
                    type="password"
                    ref="password"
                    class="rg-text-field rg-auth-field block w-full border-0 px-2 py-2 rounded"
                    :class="{ 'opacity-50 cursor-not-allowed': authenticating }"
                    autocomplete="new-password"
                    placeholder="Create Password"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                class="w-full rounded bg-purple text-white text-bold text-xs focus:outline-none py-3 px-4"
                :class="{ spinner: registering }"
                :disabled="registering || accountCreated"
                @click="submit()"
              >
                CREATE ACCOUNT
              </button>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2">
            <div class="pt-3 pr-3">
              <button
                class="w-full rounded bg-purple text-white text-bold text-xs focus:outline-none py-3 px-4"
                :class="{ spinner: registering }"
                :disabled="registering || accountCreated"
                @click="submit()"
              >
                CREATE ACCOUNT
              </button>

              <button
                class="w-full rounded border border-blue bg-white text-primary text-bold text-xs focus:outline-none py-3 px-4 mt-3"
                @click="e1 = 1"
              >
                BACK
              </button>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <div v-show="e1 == 1" class="mt-6 text-xs">
        <div class="float-right">
          Don't have an agency?

          <router-link class="no-underline align-baseline font-medium text-xs" to="/signup">
            Signup for Business
          </router-link>
        </div>

        Already have an account?
        <router-link class="no-underline align-baseline font-medium text-xs" to="/login">
          Login
        </router-link>
      </div>
    </div>
  </auth-layout>
</template>

<script>
import AgencySetupGuide from '@/components/common/AgencySetupGuide.vue'
import { mapState } from 'vuex'
import AuthLayout from '@/components/AuthLayout.vue'
import { GetSuggestions } from '@/utils/PlaceUtils'
import $ from 'jquery'
import axios from 'axios'
import { affiliateReferral, clearAffiliateReferral } from '@/utils/affiliateAttribution'

export default {
  components: {
    AgencySetupGuide,
    AuthLayout
  },

  data: function() {
    return {
      company_name: '',

      success: null,
      info: null,
      error: null,
      registering: false,
      accountCreated: false,
      authenticating: false,

      company_type: 'agency',

      business_location: {
        country: 'US'
      },

      e1: 1,

      phone: '',
      phone_valid: false,

      agree: false,

      // New product flags
      white_label_signup_enabled: true
    }
  },

  computed: {
    ...mapState({
      me: state => state.me
    })
  },

  metaInfo() {
    return {
      title: 'Sign Up | ' + this.$appName
    }
  },

  methods: {
    async submit() {
      if (this.registering || this.accountCreated) return
      this.error = null

      if (!this.white_label_signup_enabled) {
        this.error = 'Please select at least one product.'
        return
      }

      this.registering = true

      try {
        const params = {
          company_name: this.company_name.trim() || this.$refs.company_name.value.trim(),
          company_type: 'agency',
          affiliate_ref: affiliateReferral(),
          first_name: this.$refs.first_name.value.trim(),
          last_name: this.$refs.last_name.value.trim(),
          email: this.$refs.email.value.trim(),
          phone: this.$refs.phone.value.trim(),
          password: this.$refs.password.value,
          business_location: this.business_location,

          // New fields for backend
          white_label_signup_enabled: this.white_label_signup_enabled
        }

        if (window.location.origin != process.env.VUE_APP_MAIN_URL) {
          params.host = window.location.host
        }

        if (this.$route.query.u) {
          params.u = this.$route.query.u
        }

        await this.$store.dispatch('auth/signup', params)
        this.accountCreated = true
        clearAffiliateReferral()

        setTimeout(() => {
          window.location = '/onboarding'
        }, 1000)
      } catch (error) {
        this.error = (error && error.message) || 'Could not create the account. Check your details or try signing in if you already registered.'
        for (var i in error.errors) {
          if (error.errors[i] != 'null') {
            this.error = (this.error || '') + error.errors[i] + ' '
          }
        }
      }

      this.registering = false
    }
  },

  mounted() {
    let self = this

    setTimeout(function() {
      if (self.$route.query.email) {
        self.$refs.email.value = self.$route.query.email
      }
    }, 500)
  }
}
</script>

<style scoped>
.v-stepper {
  border: 0px !important;
  box-shadow: none !important;
  padding: 0px !important;
}

.v-stepper__content {
  padding: 0px !important;
}
</style>